import queryString from 'query-string';
import axios from 'axios';
import {
  __BASE_URL__,
  __DESIGN_WIDTH__,
  STORAGE_CHANNEL_KEY,
  STORAGE_SCHANNEL_KEY,
} from '@os-web/const';

import { isObject } from './is.ts';

type Resolver<T> = (value: T | PromiseLike<T>) => void;

export function parseUA() {
  const userAgent = navigator.userAgent;
  const lowerUserAgent = userAgent.toLowerCase();

  return {
    mobile: /AppleWebKit.*Mobile.*/.test(userAgent),
    ios: /\(i[^;]+;( U;)? CPU.+Mac OS X/.test(userAgent),
    android: lowerUserAgent.includes('android') || lowerUserAgent.includes('linux'),
    iPhone: userAgent.includes('iPhone'),
    iPad: userAgent.includes('iPad'),
    weixin: lowerUserAgent.includes('micromessenger'),
    wxMiniprogram:
      (lowerUserAgent.includes('micromessenger') && lowerUserAgent.includes('miniprogram')) ||
      window.__wxjs_environment === 'miniprogram',
    huawei: lowerUserAgent.includes('huawei'),
    harmony: lowerUserAgent.includes('openharmony') || lowerUserAgent.includes('harmonyos'),
    baiduMiniprogram: /swan\//.test(userAgent) || window.name.startsWith('webswan-'),
    QQbrw: lowerUserAgent.includes('mqqbrowser'),
    QQ: lowerUserAgent.includes('qq'),
    weibo: lowerUserAgent.includes('weibo'),
    baidu: lowerUserAgent.includes('baidu'),
  };
}

export function getParams(url = location.href) {
  return queryString.parseUrl(url).query;
}

export function getChannel() {
  return getParams().channel || localStorage.getItem(STORAGE_CHANNEL_KEY);
}

export function getSChannel() {
  return getParams().schannel || localStorage.getItem(STORAGE_SCHANNEL_KEY);
}

export function PromiseWithResolvers<T = void>() {
  const returns = {
    resolve: undefined as unknown as Resolver<T>,
    reject: undefined as unknown as (reason?: unknown) => void,
    state: 'pending' as 'pending' | 'fulfilled' | 'rejected',
    promise: null as unknown as Promise<T>,
  };

  returns.promise = new Promise<T>((resolve, reject) => {
    returns.resolve = (value) => {
      returns.state = 'fulfilled';
      resolve(value);
    };
    returns.reject = (reason) => {
      returns.state = 'rejected';
      reject(reason);
    };
  });

  return returns;
}

export async function sleep(ms = 0) {
  const { promise, resolve } = PromiseWithResolvers<void>();
  setTimeout(() => resolve(), ms);
  return promise;
}

export type ClientLocationInfo = {
  ip?: string;
  city?: string;
  region?: string;
  country?: string;
  country_code?: string;
  location_text?: string;
  time_zone: string;
};

let clientLocationInfoCache: ClientLocationInfo | null = null;

function getBrowserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
}

function getCountryName(countryCode = '') {
  if (!countryCode) return '';

  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
}

export async function getClientLocationInfo({ timeout = 10000, cache = true } = {}) {
  if (cache && clientLocationInfoCache) return clientLocationInfoCache;

  const defaultInfo: ClientLocationInfo = {
    time_zone: getBrowserTimeZone(),
  };

  try {
    const res = await axios.get('https://ip-api.globecul.com/json', { timeout });
    const ip = res.data || {};
    const countryCode = ip.country || '';
    const country = getCountryName(countryCode);
    const locationParts = [ip.city, ip.regionName, country].filter(Boolean);
    const info = {
      ip: ip.query || '',
      city: ip.city || '',
      region: ip.regionName || '',
      country,
      country_code: countryCode,
      location_text: locationParts.join(', '),
      time_zone: ip.timezone || defaultInfo.time_zone,
    };

    if (cache) clientLocationInfoCache = info;
    return info;
  } catch (error) {
    console.warn('ip-api.globecul.com failed, fallback to ip.011102.xyz', error);
  }

  try {
    const res = await axios.get('https://ip.011102.xyz', { timeout });
    const ip = res.data?.IP || {};
    const countryCode = ip.Country || '';
    const country = getCountryName(countryCode);
    const locationParts = [ip.City, ip.Region, country].filter(Boolean);
    const info = {
      ip: ip.IP || '',
      city: ip.City || '',
      region: ip.Region || '',
      country,
      country_code: countryCode,
      location_text: locationParts.join(', '),
      time_zone: ip.Timezone || defaultInfo.time_zone,
    };

    if (cache) clientLocationInfoCache = info;
    return info;
  } catch (error) {
    console.warn('getClientLocationInfo failed:', error);
    try {
      return defaultInfo;
    } finally {
      if (cache) clientLocationInfoCache = defaultInfo;
    }
  }
}

export function dpx2rem(value: number, { decimals = 6, unit = false } = {}) {
  const remValue = (value / Number(__DESIGN_WIDTH__)).toFixed(decimals);
  return unit ? `${remValue}rem` : remValue;
}

export function dpx2remUnit(value: number, options = {}) {
  return dpx2rem(value, { ...options, unit: true });
}

export function getProjectBaseUrl(origin: string | false = location.origin) {
  if (!origin) return __BASE_URL__;
  return `${origin}${__BASE_URL__}`;
}

export function getProjectRouteUrl(
  routePath = '',
  query: queryString.StringifiableRecord = {},
  options: queryString.StringifyOptions = {},
) {
  const url = new URL(routePath.replace(/^\/+/, ''), getProjectBaseUrl()).href;

  return queryString.stringifyUrl(
    {
      url,
      query,
    },
    options,
  );
}

export class Cache<T = unknown> {
  static DEFAULT_EXPIRED = -1;

  constructor(private readonly key: string) {}

  set(value: T, expiredTime = Cache.DEFAULT_EXPIRED) {
    localStorage.setItem(
      this.key,
      JSON.stringify({
        data: value,
        expiredTime,
        createdTime: Date.now(),
      }),
    );
  }

  get() {
    try {
      const storage = localStorage.getItem(this.key);
      if (!storage) return null;

      const item = JSON.parse(storage) as unknown;
      if (!isObject(item)) return null;

      const { data, expiredTime } = item as { data?: T; expiredTime?: number };
      if (expiredTime === Cache.DEFAULT_EXPIRED) return data ?? null;
      if (typeof expiredTime === 'number' && expiredTime > Date.now()) return data ?? null;

      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  remove() {
    localStorage.removeItem(this.key);
  }
}
